import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { extractApiMessage } from "@/shared/api/extract-message";
import { toastAdvertencia, toastError, toastErrorApi, toastExito } from "@/lib/toast";
import {
  actualizarRolesNivel,
  crearNivelAutorizacion,
  eliminarNivelAutorizacion,
  establecerGeneraPolizaEnNivel,
  type CrearNivelAutorizacionInput,
} from "../services/niveles-service";

export function useCrearNivel(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearNivelAutorizacionInput) => crearNivelAutorizacion(input),
    onSuccess: (n) => {
      toastExito({ title: `Nivel ${n.nivel} creado`, message: `${n.tipo}/${n.subtipo}` });
      void qc.invalidateQueries({ queryKey: qk.adminFlujosAutorizacion });
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

/**
 * Crea el mismo nivel (tipo/subtipo/nivel/roles) en N áreas de una sola
 * acción del admin · llama `crearNivelAutorizacion` por cada área en
 * paralelo, recolecta éxitos/fallas y muestra una sola notificación
 * agregada. Si una falla por duplicado u otro motivo, el resto se crea.
 */
export function useCrearNivelEnAreas(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Omit<CrearNivelAutorizacionInput, "areaId"> & { areaIds: string[] }
    ) => {
      const { areaIds, ...resto } = input;
      const resultados = await Promise.allSettled(
        areaIds.map((areaId) =>
          crearNivelAutorizacion({ ...resto, areaId })
        )
      );
      const creados = resultados.filter((r) => r.status === "fulfilled").length;
      const fallidos = resultados
        .map((r, idx) => ({ r, areaId: areaIds[idx] }))
        .filter(({ r }) => r.status === "rejected")
        .map(({ r, areaId }) => ({
          areaId,
          mensaje: extractApiMessage(
            (r as PromiseRejectedResult).reason,
            "Error desconocido"
          ),
        }));
      return { creados, fallidos };
    },
    onSuccess: ({ creados, fallidos }) => {
      void qc.invalidateQueries({ queryKey: qk.adminFlujosAutorizacion });
      if (creados > 0 && fallidos.length === 0) {
        toastExito({
          title: creados === 1 ? "Nivel creado" : `${creados} niveles creados`,
          message: "Uno por cada área seleccionada.",
        });
      } else if (creados > 0 && fallidos.length > 0) {
        toastAdvertencia({
          title: `${creados} creados · ${fallidos.length} fallaron`,
          message: fallidos.map((f) => f.mensaje).slice(0, 3).join(" · "),
        });
      } else if (fallidos.length > 0) {
        toastError({
          title: "No se pudieron crear los niveles",
          message: fallidos.map((f) => f.mensaje).slice(0, 3).join(" · "),
        });
      }
      if (creados > 0) {
        opts?.onCreado?.();
      }
    },
    onError: toastErrorApi,
  });
}

export function useActualizarRolesNivel(opts?: { onActualizado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      roles,
      generaPolizaAlAprobar,
    }: {
      id: string;
      roles: string[];
      generaPolizaAlAprobar: boolean;
    }) => actualizarRolesNivel(id, roles, generaPolizaAlAprobar),
    onSuccess: () => {
      toastExito({ title: "Roles actualizados", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminFlujosAutorizacion });
      opts?.onActualizado?.();
    },
    onError: toastErrorApi,
  });
}

/**
 * Aplica el flag "generar póliza al aprobar" a un nivel en TODAS las áreas de un
 * flujo (tipo/subtipo/nivel) de una sola acción · una llamada atómica al servidor.
 */
export function useEstablecerGeneraPolizaEnTodasLasAreas(opts?: { onAplicado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      tipo: string;
      subtipo: string;
      nivel: number;
      generaPolizaAlAprobar: boolean;
    }) => establecerGeneraPolizaEnNivel(input),
    onSuccess: (afectados, vars) => {
      toastExito({
        title: vars.generaPolizaAlAprobar
          ? "Póliza al aprobar activada en todas las áreas"
          : "Póliza al aprobar desactivada en todas las áreas",
        message: `${afectados} nivel(es) actualizado(s) · ${vars.tipo}/${vars.subtipo} nivel ${vars.nivel}.`,
      });
      void qc.invalidateQueries({ queryKey: qk.adminFlujosAutorizacion });
      opts?.onAplicado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEliminarNivel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; nivel: number }) =>
      eliminarNivelAutorizacion(id),
    onSuccess: (_data, vars) => {
      toastExito({ title: `Nivel ${vars.nivel} eliminado`, message: "Las solicitudes en autorización no se afectan." });
      void qc.invalidateQueries({ queryKey: qk.adminFlujosAutorizacion });
    },
    onError: toastErrorApi,
  });
}
